import { Table } from 'antd';
import type { ColumnsType } from 'antd/lib/table';
import moment from 'moment-timezone';
import { observer } from 'mobx-react';
import React, { useMemo } from 'react';
import { useBusinessUnitStore } from '../../../contexts/mobx';
import { Doc31CouponPromotion } from '../../../types/doc31';
import {
    CouponIdentity,
    PacingBars,
    ProvincesCell,
    StatusTags,
    StockCell,
} from './coupon-cells';
import { drawShare } from './form';
import style from './style.module.scss';

interface CouponTableProps {
    coupons: Doc31CouponPromotion[];
    timezone: string;
    totalWeight: number;
    loading: boolean;
    onOpen: (promotionId: number) => void;
}

export const CouponTable = observer(
    ({ coupons, timezone, totalWeight, loading, onOpen }: CouponTableProps) => {
        const { businessUnits } = useBusinessUnitStore();
        const provinceNames = useMemo(
            () =>
                businessUnits
                    ? new Map(
                          businessUnits.flatMap((unit) =>
                              unit.provinces.map(
                                  (province) =>
                                      [province.id, province.name] as const
                              )
                          )
                      )
                    : null,
            [businessUnits]
        );

        const columns: ColumnsType<Doc31CouponPromotion> = [
            {
                title: 'Coupon',
                key: 'coupon',
                render: (_, coupon) => <CouponIdentity coupon={coupon} />,
            },
            {
                title: 'Provinces',
                key: 'provinces',
                render: (_, coupon) => (
                    <ProvincesCell
                        ids={coupon.provinceIds}
                        names={provinceNames}
                    />
                ),
            },
            {
                title: 'Dates',
                key: 'dates',
                render: (_, coupon) => (
                    <span className={`${style.numeric} ${style.nowrap}`}>
                        {moment
                            .tz(coupon.startDate, timezone)
                            .format('MMM D, HH:mm')}{' '}
                        →{' '}
                        {moment
                            .tz(coupon.endDate, timezone)
                            .format('MMM D, HH:mm')}
                    </span>
                ),
            },
            {
                title: 'Stock',
                key: 'stock',
                render: (_, coupon) => <StockCell coupon={coupon} />,
            },
            {
                title: 'Odds',
                key: 'odds',
                render: (_, coupon) => {
                    const share =
                        coupon.selection.status === 'active'
                            ? drawShare(coupon.selection.weight, totalWeight)
                            : null;
                    return share === null ? (
                        <span className={style.muted}>—</span>
                    ) : (
                        <div className={style.odds}>
                            <span className={style.numeric}>{share}</span>
                            <span
                                className={`${style.numeric} ${style.caption}`}
                            >
                                Weight {coupon.selection.weight}
                            </span>
                        </div>
                    );
                },
            },
            {
                title: 'Pacing',
                key: 'pacing',
                render: (_, coupon) => <PacingBars coupon={coupon} />,
            },
            {
                title: 'Status',
                key: 'status',
                render: (_, coupon) => <StatusTags coupon={coupon} />,
            },
        ];

        return (
            <Table
                className={style.table}
                rowKey="promotionId"
                columns={columns}
                dataSource={coupons}
                loading={loading}
                pagination={false}
                rowClassName={style.row}
                onRow={(coupon) => ({
                    tabIndex: 0,
                    onClick: () => onOpen(coupon.promotionId),
                    onKeyDown: (event) => {
                        if (event.key === 'Enter') {
                            onOpen(coupon.promotionId);
                        }
                    },
                })}
            />
        );
    }
);
